import React, { useMemo } from 'react'
import { Users } from 'lucide-react'
import Card from '../Card'
import { useNavigate } from 'react-router-dom'
import DataTable, { Column } from '../DataTable'

interface User {
  id: string
  firstName: string
  lastName?: string
  email: string
  phone?: string
  createdAt: string
  totalOrders: number
  totalSpent: number
}

interface RecentUsersTableProps {
  users: User[]
  loading?: boolean
}

export default function RecentUsersTable({ users, loading = false }: RecentUsersTableProps) {
  const navigate = useNavigate()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getInitials = (firstName: string, lastName?: string) => {
    return ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase()
  }

  const columns: Column<User>[] = useMemo(
    () => [
      {
        key: 'firstName',
        header: 'Name',
        render: (value, row) => (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6D3A0D] text-white flex items-center justify-center font-semibold text-sm">
              {getInitials(row.firstName, row.lastName)}
            </div>
            <span>{row.firstName} {row.lastName || ''}</span>
          </div>
        ),
        sortable: true,
        sortType: 'text',
      },
      {
        key: 'email',
        header: 'Email',
        sortable: true,
        sortType: 'text',
      },
      {
        key: 'totalOrders',
        header: 'Orders',
        sortable: true,
        sortType: 'number',
      },
      {
        key: 'totalSpent',
        header: 'Total Spent',
        render: (value) => `₹${Number(value).toFixed(2)}`,
        sortable: true,
        sortType: 'price',
      },
      {
        key: 'createdAt',
        header: 'Joined',
        render: (value) => formatDate(value),
        sortable: true,
        sortType: 'date',
      },
    ],
    []
  )

  if (loading) {
    return (
      <Card title="Recent Users" subtitle="Latest 10 registered customers">
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </Card>
    )
  }

  if (!users || users.length === 0) {
    return (
      <Card title="Recent Users" subtitle="Latest 10 registered customers">
        <div className="py-12 flex flex-col items-center justify-center text-gray-500">
          <Users size={40} className="mb-2 opacity-50" />
          <p>No users registered yet</p>
        </div>
      </Card>
    )
  }

  return (
    <Card title="Recent Users" subtitle="Latest 10 registered customers">
      <DataTable<User>
        columns={columns}
        data={users}
        onRowClick={(user) => navigate(`/customers/${user.id}`)}
      />
      <button
        onClick={() => navigate('/customers')}
        className="mt-4 w-full py-2 text-center text-[#6D3A0D] font-medium hover:bg-gray-50 rounded-lg transition-colors"
      >
        View All Users
      </button>
    </Card>
  )
}
